import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames/bind'
import {
  Button,
  Icon,
  Image,
  Grid,
  PageLayout,
  Text,
  useApplicationTheme,
  ProductLine,
} from '@adc/ui-core'
import adcLogo from '../../img/auction-logo.svg'
import googlePlayLogo from './img/google_play.png'
import appStoreLogo from './img/app_store.png'
import ccpaOptLogo from './img/ccpa_icon.png'
import logoEHOSrc from './img/logo_eho_2x.png'
import cls from './styles.scss'
import { useApplicationContext } from '../auctionApplication/context'
const cx = classNames.bind(cls)

const LinkDivider = ({ breakLineOnMobile = false }) => (
  <>
    <span className={cx({ 'show-on-computer-or-larger': breakLineOnMobile })}>
      <span className={cx('vert-bar', 'right')} />
      <span className={cx('vert-bar')} />
    </span>
    {breakLineOnMobile && (
      <br
        className={cx({ 'show-on-smaller-than-computer': breakLineOnMobile })}
      />
    )}
  </>
)

LinkDivider.propTypes = {
  breakLineOnMobile: PropTypes.bool,
}

const SocialLinks = ({ className, isMobile }) => {
  const elmIdSuffix = isMobile ? 'small' : 'large'
  return (
    <div className={cx('social-links', className)}>
      <Button
        target='_blank'
        link='https://www.linkedin.com/company/auction'
        role='navigation'
        className={cx('link-icon')}
        elmId={`footer_linkedin_${elmIdSuffix}`}
        aria-label='LinkedIn'
      >
        <Icon.Group size='big'>
          <Icon name='circle' iconStyle='solid' color='adcLightBlue' />
          <Icon
            name='linkedin-in'
            iconStyle='brand'
            size='tiny'
            color='white'
            className={cx('inner-icon')}
          />
        </Icon.Group>
      </Button>
      <Button
        target='_blank'
        link='https://www.youtube.com/user/auctionsnow'
        role='navigation'
        className={cx('link-icon')}
        elmId={`footer_youtube_${elmIdSuffix}`}
        aria-label='Youtube'
      >
        <Icon.Group size='big'>
          <Icon name='circle' iconStyle='solid' color='adcLightBlue' />
          <Icon
            name='youtube'
            iconStyle='brand'
            size='tiny'
            color='white'
            className={cx('inner-icon')}
          />
        </Icon.Group>
      </Button>
    </div>
  )
}

SocialLinks.propTypes = {
  className: PropTypes.string,
  isMobile: PropTypes.bool,
}

const Footer = ({
  className,
  useDeprecatedPageWidth,
  theme: themeInjected,
  embeddedContext: embeddedContextInjected,
}) => {
  /**
   * theme and embeddedContext can be injected by parent component, e.g in the case of a DropKit web component.
   * Otherwise, we'll look for the appropriate React context for that info.
   */
  const themeFromContext = useApplicationTheme()
  const theme = themeInjected || themeFromContext
  const { embeddedContext: embeddedContextFromAppContext } =
    useApplicationContext()
  const embeddedContext =
    embeddedContextInjected || embeddedContextFromAppContext

  const [shouldDisplayMobileStoreLinks, setShouldDisplayMobileStoreLinks] =
    useState(true)

  useEffect(() => {
    // We hide the app store links from view if the user is navigating from the mobile app to avoid apple rejecting the app submission
    if (embeddedContext === 'webview') {
      setShouldDisplayMobileStoreLinks(false)
    }
  }, [embeddedContext])

  return (
    <footer className={cx('root', className)}>
      <PageLayout useDeprecatedPageWidth={useDeprecatedPageWidth}>
        <Grid>
          {theme.productLine === ProductLine.AuctionCom && (
            <>
              <Grid.Row>
                <Grid.Column
                  mobile={16}
                  tablet={16}
                  computer={16}
                  largeScreen={shouldDisplayMobileStoreLinks ? 12 : 16}
                  widescreen={shouldDisplayMobileStoreLinks ? 12 : 16}
                >
                  <div className={cx('main-bar')}>
                    <div className={cx('main-links')}>
                      <Button
                        link='https://www.auction.com'
                        role='navigation'
                        className={cx('store-button')}
                      >
                        <Image
                          className={cx('icon')}
                          src={adcLogo}
                          data-elm-id='f_logo'
                          alt='auction.com logo'
                        />
                      </Button>
                      <Button
                        size='small'
                        target='_blank'
                        link='https://www.auction.com/lp/about-us/'
                        role='navigation'
                        elmId='footer_about_us'
                        className={cx('link-button')}
                      >
                        About Us
                      </Button>
                      <Button
                        size='small'
                        target='_blank'
                        link='https://www.auction.com/lp/careers/'
                        role='navigation'
                        elmId='footer_careers'
                        className={cx('link-button')}
                      >
                        Careers
                      </Button>
                      <Button
                        size='small'
                        target='_blank'
                        link='https://www.auction.com/lp/contact-us/'
                        role='navigation'
                        elmId='footer_contact_us'
                        className={cx('link-button')}
                      >
                        Contact Us
                      </Button>
                      <SocialLinks
                        className='show-on-smaller-than-computer'
                        isMobile
                      />{' '}
                    </div>
                    <SocialLinks className='show-on-computer-or-larger' />
                  </div>
                </Grid.Column>
                {shouldDisplayMobileStoreLinks && (
                  <Grid.Column
                    mobile={16}
                    tablet={16}
                    computer={16}
                    largeScreen={4}
                    widescreen={4}
                  >
                    <div className={cx('store-links')}>
                      <Button
                        target='_blank'
                        link='https://auction.onelink.me/WqvL/462df8a'
                        role='navigation'
                        className={cx('store-button')}
                        elmId='footer_apple_appstore'
                      >
                        <Image
                          src={appStoreLogo}
                          alt='app store'
                          className={cx('store-button-image')}
                        />
                      </Button>
                      <Button
                        target='_blank'
                        link='https://auction.onelink.me/WqvL/c9539c45'
                        role='navigation'
                        className={cx('store-button')}
                        elmId='footer_google_playstore'
                      >
                        <Image
                          src={googlePlayLogo}
                          alt='google play'
                          className={cx('store-button-image')}
                        />
                      </Button>
                    </div>
                  </Grid.Column>
                )}
              </Grid.Row>
              <div className={cx('divider')} />
            </>
          )}
          <Grid.Row>
            <Grid.Column
              className={cx('sub-links')}
              mobile={16}
              tablet={16}
              computer={16}
              largeScreen={13}
              widescreen={13}
            >
              <div className={cx('sub-bar')}>
                <Button
                  size='small'
                  target='_blank'
                  link='https://www.auction.com/lp/legal/privacy/'
                  role='navigation'
                  elmId='footer_privacy_statement'
                  className={cx('link-button', 'sub-link')}
                >
                  Privacy Statement
                </Button>
                <LinkDivider />
                <Button
                  size='small'
                  id='ot-sdk-btn'
                  role='navigation'
                  elmId='footer_cookie_preferences'
                  className={cx(
                    'link-button',
                    'sub-link',
                    'onetrust-clear',
                    'ot-sdk-show-settings'
                  )}
                >
                  Cookie Preferences
                </Button>
                <LinkDivider />
                <Button
                  size='small'
                  target='_blank'
                  link='https://privacyportal.onetrust.com/webform/8d2beebc-7be2-456a-9bcb-4b0a3fcfcdd7/draft/70e0f70d-a61c-4c73-b46d-a609cbad7b34'
                  role='navigation'
                  elmId='footer_your_privacy_choices'
                  className={cx('link-button', 'sub-link')}
                >
                  Your Privacy Choices
                  <Image
                    src={ccpaOptLogo}
                    alt='California Consumer Privacy Act (CCPA) Opt-Out Icon'
                    className={cx('link-button', 'sub-link', 'inline-logo')}
                    data-elm-id='ccpa-logo'
                  />
                </Button>
                <LinkDivider breakLineOnMobile />
                <Button
                  size='small'
                  target='_blank'
                  link='https://www.auction.com/lp/legal/terms/'
                  role='navigation'
                  elmId='footer_terms_of_use'
                  className={cx('link-button', 'sub-link')}
                >
                  Terms of Use
                </Button>
                <LinkDivider />
                <Button
                  size='small'
                  target='_blank'
                  link='https://www.auction.com/lp/legal/participation-terms/'
                  role='navigation'
                  elmId='footer_participation_terms'
                  className={cx('link-button', 'sub-link')}
                >
                  Participation Terms
                </Button>
                <LinkDivider />
                <Button
                  size='small'
                  target='_blank'
                  link='https://www.auction.com/lp/legal/licensing-disclosures/'
                  role='navigation'
                  elmId='footer_licensing_disclosures'
                  className={cx('link-button', 'sub-link')}
                >
                  Licensing & Disclosures
                </Button>
                <LinkDivider />
                <Button
                  size='small'
                  target='_blank'
                  link='https://www.auction.com/lp/accessibility-statement/'
                  role='navigation'
                  elmId='footer_accessibility'
                  className={cx('link-button', 'sub-link')}
                >
                  Accessibility
                </Button>
                {theme.productLine === ProductLine.AuctionCom && (
                  <>
                    <LinkDivider />
                    <Button
                      size='small'
                      target='_blank'
                      link='https://www.sparkoffer.com/'
                      role='navigation'
                      elmId='footer_sparkoffer'
                      className={cx('link-button', 'sub-link')}
                    >
                      SparkOffer
                    </Button>
                  </>
                )}
                <br />
                {/* <div
                  className={cx('divider', 'show-on-smaller-than-computer')}
                /> */}
              </div>
            </Grid.Column>
            <Grid.Column
              className={cx('sub-links')}
              mobile={16}
              tablet={16}
              computer={16}
              largeScreen={3}
              widescreen={3}
            >
              <div
                className={cx('fe-footer-copyright-content')}
                style={{
                  flexWrap: useDeprecatedPageWidth ? 'wrap-reverse' : 'initial',
                }}
              >
                <span className={cx('text')} data-elm-id='footer_year_company'>
                  &copy; {new Date().getFullYear()} Auction.com, LLC.
                </span>
                <img
                  src={logoEHOSrc}
                  data-elm-id='logo_eho'
                  className={cx('fe-footer-copyright-logo')}
                  alt='footer-logo'
                />
              </div>
            </Grid.Column>
          </Grid.Row>
          <div className={cx('divider')} />
          <Grid.Row>
            <Grid.Column className={cx('notices')}>
              <Text size='mini' color='gray' style={{ lineHeight: '20px' }}>
                <a
                  href='https://adc-tenbox-prod.imgix.net/static/A8F10DAB56CD43049CBD2756649015D7.v1.pdf'
                  target='_blank'
                  rel='noreferrer'
                  data-elm-id='footer_ny_standardized_operating_procedures'
                >
                  NY Standardized Operating Procedures
                </a>
              </Text>
              <Text size='mini' color='gray' style={{ lineHeight: '20px' }}>
                <a
                  href='https://dos.ny.gov/fair-housing-notice'
                  target='_blank'
                  rel='noreferrer'
                  data-elm-id='footer_ny_standardized_operating_procedures'
                >
                  New York Fair Housing Notice
                </a>
              </Text>
              <Text
                size='mini'
                color='gray'
                style={{ lineHeight: '20px', textAlign: 'center' }}
              >
                TREC:{' '}
                <a
                  href='https://www.auction.com/lp/wp-content/uploads/files/lp/mlhdocs.com/legal/agencydisclosures/ADC/Texas.pdf'
                  target='_blank'
                  rel='noreferrer'
                  data-elm-id='footer_ny_standardized_operating_procedures'
                >
                  Information About Brokerage Services
                </a>
                ;{' '}
                <a
                  href='https://www.auction.com/lp/wp-content/uploads/files/lp/mlhdocs.com/legal/TexasCPN.pdf'
                  target='_blank'
                  rel='noreferrer'
                  data-elm-id='footer_ny_standardized_operating_procedures'
                >
                  Texas Consumer Protection Notice
                </a>
              </Text>
              <Text size='mini' color='gray' style={{ lineHeight: '20px' }}>
                California DRE # 01093886; WV: Lee Leslie, Broker
              </Text>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </PageLayout>
    </footer>
  )
}

Footer.propTypes = {
  className: PropTypes.string,
  useDeprecatedPageWidth: PropTypes.bool,
  theme: PropTypes.object,
  embeddedContext: PropTypes.string,
}

export default Footer
